<template>
  <div class="opinion-container">
    <div class="opinion-content">
      <p
        class="description"
        v-html="opinion.description"
      />
      <div
        v-if="opinion.medias?.length"
        class="medias"
      >
        <h5>Photos et vidéos du Waper</h5>
        <div class="medias-container">
          <div
            v-for="(media, index) in opinion.medias"
            :key="`media-${index}`"
            class="media-thumb"
            @click="mediaZoom = media"
          >
            <img
              v-if="media.type === 1"
              :src="media.url_media"
              :alt="`image #${index} de ${opinion.title}`"
              loading="lazy"
            />
            <img
              v-if="media.type === 2 && media.url_media"
              :src="getThumbnail(media.url_media)"
              :alt="`image #${index} de ${opinion.title}`"
              loading="lazy"
            />
            <img
              v-if="media.type === 2"
              src="~/assets/icons/youtube_dark.svg"
              alt="logo youtube"
              class="play"
              loading="lazy"
            />
          </div>
        </div>
      </div>
      <div class="gauges">
        <div
          v-for="(category, index) in opinion.categories"
          :key="`category-${index}`"
          class="category"
        >
          <div class="text">
            <p class="title">{{ category.title }}</p>
            <p class="score">{{ numberToString(category.score) }}</p>
          </div>
          <div class="gauge">
            <div
              class="innerGauge"
              :style="{ width: `${(category.score / 5) * 100}%` }"
            />
          </div>
        </div>
      </div>
      <div
        v-if="opinion.recommendation"
        class="recommendation"
      >
        <div class="icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 11.189 9.739"
          >
            <g transform="translate(0.001 0.002)">
              <path
                d="M3.182,9.464a.944.944,0,0,0,1.4-.075l6.4-7.851A.944.944,0,1,0,9.514.345L3.78,7.4,1.607,5.253A.944.944,0,0,0,.28,6.595l2.9,2.87"
                transform="translate(0 0)"
                fill="#fff"
                fill-rule="evenodd"
              />
            </g>
          </svg>
        </div>
        <p>Je recommande ce produit à mes amis</p>
      </div>
      <div
        v-else
        class="recommendation red"
      >
        <div class="icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 10.5 10.5"
          >
            <g transform="translate(-130.379 -20.545)">
              <path
                d="M131.6,20.753a.713.713,0,1,0-1.008,1.008l9.074,9.074a.713.713,0,1,0,1.008-1.008Z"
                transform="translate(0 0)"
                fill="#fff"
              />
              <path
                d="M130.587,29.828a.713.713,0,1,0,1.008,1.008l9.074-9.074a.713.713,0,1,0-1.008-1.008Z"
                transform="translate(0 0)"
                fill="#fff"
              />
            </g>
          </svg>
        </div>
        <p>Je ne recommande pas ce produit à mes amis</p>
      </div>
      <div class="answers">
        <div
          v-if="opinion.ps_version"
          class="answer-item answer-console"
        >
          <p class="label">Sur quelle console avez-vous joué à ce jeu ?</p>
          <p class="answer">
            <img
              :src="`/img/logo-${opinion.ps_version.toLowerCase()}.svg`"
              :alt="`logo ${opinion.ps_version.toUpperCase()}`"
              loading="lazy"
              width="92"
              height="20"
              class="svg"
            />
          </p>
        </div>
        <div
          v-for="answer in opinion.answers"
          :key="answer.answer"
          class="answer-item"
        >
          <p class="label">{{ answer.label }}</p>
          <p class="answer">{{ answer.answer }}</p>
        </div>
      </div>
    </div>
    <ZoomMedia
      v-if="mediaZoom"
      :media="mediaZoom"
      @close="resetMediaZoom"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { extractVideoId } from '~/utils/helpers'
import type { Opinion } from '~/types'

defineProps<{
  opinion: Opinion
}>()

const mediaZoom = ref<NonNullable<Opinion['medias']>[number]>()

const resetMediaZoom = () => {
  mediaZoom.value = undefined
}

const getThumbnail = (url: string) => {
  const info = extractVideoId(url)

  if (!info) return

  switch (info.source) {
    case 'youtube':
      return `https://img.youtube.com/vi/${info.id}/maxresdefault.jpg`

    case 'dailymotion':
      return `https://www.dailymotion.com/thumbnail/video/${info.id}`

    case 'vimeo':
      {
        const { data, error } = useFetch(
          `http://vimeo.com/api/v2/video/${info.id}.json`,
          {
            query: {
              callback: 'showThumb',
            },
          },
        )
        const rawData = unref(data)

        if (error.value || rawData) {
          logError(error)
        }
      }
      break
    default:
      break
  }
}
</script>

<style lang="scss" scoped>
.opinion-container {
  flex-grow: 1;
  padding-top: 0;

  .opinion-content {
    margin-top: 4rem;

    .description {
      white-space: pre-line;
    }

    h4 {
      margin-bottom: 2.5rem;
      font-size: 1.8rem;
      font-weight: 500;
      line-height: 1.8rem;
    }

    .medias {
      margin-top: 4rem;

      h5 {
        margin-bottom: 1.5rem;
        font-size: 1.4rem;
        font-weight: 500;
      }

      .medias-container {
        display: flex;
        flex-wrap: wrap;

        .media-thumb {
          position: relative;
          width: 22.6rem;
          height: 13.2rem;
          margin-top: 1.5em;
          margin-right: 1.5em;
          cursor: pointer;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .play {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 5.9rem;
            height: 4.1rem;
            transform: translate(-50%, -50%);
          }
        }
      }
    }

    .gauges {
      margin-top: 6rem;
      margin-bottom: 4rem;

      .category {
        width: 100%;
        margin-bottom: 1.2rem;

        .text {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.4rem;

          .title {
            font-size: 1.4rem;
            line-height: 1.8rem;
          }

          .score {
            font-size: 2.6rem;
            line-height: 3.4rem;
          }
        }

        .gauge {
          position: relative;
          width: 100%;
          height: 1rem;
          background-color: rgba(#ededed, 0.77);
          border-radius: 0.5rem;

          .innerGauge {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background: rgb(21 163 216);
            background: linear-gradient(
              270deg,
              rgba(21 163 216 / 1) 0%,
              rgba(17 114 206 / 1) 100%
            );
            border-radius: 0.5rem;
          }
        }
      }
    }

    .recommendation {
      display: flex;
      align-items: center;
      margin-bottom: 4rem;

      &.red {
        .icon {
          background: $red;

          svg {
            width: 1.05rem;
            height: 1.05rem;
          }
        }
      }

      .icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 3.5rem;
        height: 3.5rem;
        margin-right: 2rem;
        background: $blue;
        border-radius: 1.8rem;

        svg {
          width: 1.12rem;
          height: 0.974rem;
        }
      }

      p {
        font-size: 1.2rem;
        line-height: 1.6rem;
      }
    }

    .answers {
      margin-bottom: 4rem;
      border-bottom: 1px solid rgba($black, 0.2);

      .answer-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 5.4rem;
        border-top: 1px solid rgba($black, 0.2);

        .label {
          font-size: 1.2rem;
          line-height: 1.6rem;
        }

        .answer {
          font-size: 1.2rem;
          font-weight: bold;
          line-height: 1.6rem;
          text-align: right;

          svg,
          .svg {
            width: 9.2rem;
            height: 2rem;

            @media screen and (max-width: 440px) {
              width: 7.1rem;
              height: 1.5rem;
            }
          }
        }
      }
    }

    .report {
      text-align: right;

      &:hover {
        color: $blue;
      }
    }
  }
}

@media screen and (max-width: 550px) {
  .opinion-container {
    .opinion-content .medias .medias-container .media-thumb {
      width: 100%;
      height: 55vw;
      margin-right: 0;
      margin-bottom: 1.5rem;
    }
  }
}
</style>
